import React, { useEffect, useState } from 'react';
import { RegistrationFormValues, StepProps } from './index';
import { Button, Checkbox, Col, Form, Input, Row, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';
import './index.scss';

import { Title, Text, Link } from 'src/shared/components/typography';
import { isPersonalEmailAddress } from 'src/shared/utils/strings';
import { RuleObject } from 'antd/es/form';
import axios from 'axios';
import PrivacyPolicy from 'src/shared/components/legal/privacy-policy';
import UseAgreement from 'src/shared/components/legal/use-agreement';
import { IconX } from '@tabler/icons-react';
import { useExternalProviders } from './hooks/use-external-providers';
import ExternalProviders from './components/external-providers';
import { buildPasswordStrengthMessage, getPasswordStrength, getPasswordTracker } from 'src/common/password';
import { useSearchParams } from 'react-router-dom';

const SingleStepRegistration: React.FC<StepProps> = ({ onNext, loading, data, setLoading }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RegistrationFormValues>();
  const [password, setPassword] = useState(data.password || '');
  const [meter, setMeter] = useState((data.password || '').length > 0);
  const [tcAccepted, setTcAccepted] = useState<boolean>(!!data?.termsAccepted);

  const [openModal, setOpenModal] = useState<'None' | 'PrivacyPolicy' | 'UseAgreement'>('None');
  const email = data?.email;

  useEffect(() => {
    if (email !== form.getFieldValue(nameof<RegistrationFormValues>('email'))) {
      form.setFieldsValue({ [nameof<RegistrationFormValues>('email')]: email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const { externalProviders } = useExternalProviders(setLoading);

  const [searchParams] = useSearchParams();
  const draftShortlistId = searchParams.get('sid');

  const returnUrl =
    `${window.REACT_APP_WEBSITE_BASE_URI}/default-user-page` + (draftShortlistId ? `?sid=${encodeURIComponent(draftShortlistId)}` : '');

  const passwordTracker = getPasswordTracker(password);
  const passwordStrength = getPasswordStrength(passwordTracker);

  const buildMessage = () => buildPasswordStrengthMessage(passwordTracker);

  const validateEmail = async (_: RuleObject, value: string) => {
    if (!window.REACT_APP_ALLOW_PERSONAL_EMAILS_FOR_ENTERPRISE_USERS && isPersonalEmailAddress(value)) {
      return Promise.reject(t('Por favor ingrese un correo electrónico válido que no sea personal'));
    }
    return Promise.resolve();
  };

  const IsEmailDisposable = async (email: string): Promise<boolean> => {
    setLoading(true);
    try {
      const result = await axios.get<{ isDisposable: boolean }>(`/api/v1/emailValidator/isDisposable?email=${email}`);
      return result?.data?.isDisposable ?? false;
    } catch (e) {
      console.error('Registration > IsEmailDisposable', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: RegistrationFormValues) => {
    if (await IsEmailDisposable(values.email)) {
      form.setFields([
        {
          name: nameof<RegistrationFormValues>('email'),
          errors: [t('Por favor ingrese un correo electrónico válido que no sea temporal o desechable')]
        }
      ]);
      return;
    }

    const payload = {
      email: values.email,
      password: values.password,
      // Sin normalizar: lo hace `onFinish` en index.tsx, que es quien construye la peticion y el unico
      // que tiene que saber que el servidor distingue "ausente" de "sin marcar".
      termsAccepted: values.termsAccepted
    };

    onNext(payload);
  };

  const [ok, setOk] = useState<boolean>(false);

  return (
    <div id="single-step-registration">
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Title style={{ fontSize: 28, margin: 0 }} level={4}>
          {t('Accede a estos candidatos')}
        </Title>
      </div>
      <Form
        form={form}
        id="registration"
        name="registration"
        initialValues={data}
        onFieldsChange={(_, allFields) => {
          const isFormValid = allFields.every((x) => {
            if ((x as any).name?.[0] === 'termsAccepted') return !!x.value;
            return x.value && (x.errors || []).length === 0;
          });
          setOk(isFormValid && passwordStrength >= 5);
        }}
        onFinish={onFinish}
      >
        <Row gutter={[0, 20]} style={{ marginTop: 29 }}>
          <Col span={24}>
            <Form.Item
              style={{ marginBottom: 4 }}
              name={nameof<RegistrationFormValues>('email')}
              rules={[{ required: true, message: t('Por favor ingrese un email válido') as string, type: 'email' }, { validator: validateEmail }]}
            >
              <Input
                className="j2l-form-big-input"
                disabled={loading}
                size="large"
                placeholder={t('Correo electrónico de empresa')}
                prefix={<MailOutlined />}
                type="email"
                autoComplete="email"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              style={{ marginBottom: 8 }}
              extra={
                meter && (
                  <div>
                    <div className="password-strength-meter"></div>
                    <div style={{ fontSize: 13, color: '#7F88A1', lineHeight: 1.15 }}>{buildMessage()}</div>
                  </div>
                )
              }
              name={nameof<RegistrationFormValues>('password')}
              rules={[{ required: true, message: t('Por favor ingrese una contraseña') as string }]}
            >
              <Input.Password
                className="j2l-form-big-input"
                onFocus={() => setMeter(true)}
                onChange={(v) => setPassword(v.target.value)}
                disabled={loading}
                size="large"
                placeholder={t('Contraseña') as string}
                prefix={<LockOutlined />}
                autoComplete="new-password"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item style={{ marginBottom: 0 }} name={nameof<RegistrationFormValues>('termsAccepted')} valuePropName="checked">
              <Checkbox disabled={loading} onChange={(e) => setTcAccepted(e.target.checked)}>
                <Text fontSize={16}>{t('Para continuar acepta nuestras')}</Text>{' '}
                <Link
                  color="#1A1C21"
                  fontSize={16}
                  underline
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal('UseAgreement');
                  }}
                >
                  {t('condiciones de uso')}
                </Link>{' '}
                <Text fontSize={16}>{t('y')}</Text>{' '}
                <Link
                  color="#1A1C21"
                  fontSize={16}
                  underline
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal('PrivacyPolicy');
                  }}
                >
                  {t('política de privacidad')}
                </Link>
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Button
              shape="round"
              style={{
                width: '100%',
                border: 'none',
                backgroundColor: !ok || loading ? '#F1EDE9' : undefined,
                color: !ok || loading ? '#CBCED4' : undefined
              }}
              size="large"
              type="primary"
              disabled={!ok || loading}
              htmlType="submit"
            >
              {t('Acceder a la shortlist')}
            </Button>
          </Col>
        </Row>
        <ExternalProviders
          providers={externalProviders}
          loading={loading}
          tcAccepted={tcAccepted}
          returnUrl={returnUrl}
          role={data?.role}
          setLoading={setLoading}
        />
      </Form>
      <Drawer
        closeIcon={<IconX size={20} />}
        title={openModal === 'PrivacyPolicy' ? t('Política de privacidad') : t('Condiciones de uso')}
        placement="right"
        onClose={() => setOpenModal('None')}
        open={openModal !== 'None'}
        styles={{ wrapper: { width: '100%' }, header: { border: 'none' } }}
      >
        {openModal === 'PrivacyPolicy' && <PrivacyPolicy title1FontSize={38} title2FontSize={30} normalTextFontSize={18} />}
        {openModal === 'UseAgreement' && <UseAgreement title1FontSize={38} title2FontSize={30} title3FontSize={20} normalTextFontSize={18} />}
      </Drawer>
      <style>
        {`
          .password-strength-meter {
            position: relative;
            height: 4px;
            background-color: #E8E8E8;
            margin: 8px 0;
            border-radius: 2px;
          }
          .password-strength-meter::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            border-radius: 2px;
            transition: width 0.3s ease, background-color 0.3s ease;
            background-color: ${['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][passwordStrength - 1] || ''};
            width: ${(passwordStrength / 5) * 100}%;
          }
        `}
      </style>
    </div>
  );
};

export default SingleStepRegistration;
